/**
 * Enhanced File Upload Component with Post Type Support
 * Provides file upload with validation, preview, and post-type specific handling
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { PostType, EnhancedFileUploadRequest } from '../../types/postTypes';
import { useFileUpload, usePostTypeValidation } from '../../hooks/usePostTypes';
import Button from '../bootstrap/Button';
import Card, { CardBody } from '../bootstrap/Card';
import Icon from '../icon/Icon';
import Alert from '../bootstrap/Alert';
import Progress from '../bootstrap/Progress';
import Badge from '../bootstrap/Badge';

export interface EnhancedFileUploadProps {
  postType?: PostType;
  fileCategory?: 'file' | 'thumbnail';
  onFileUploaded: (filePath: string, metadata?: any) => void;
  onValidationError?: (errors: string[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  maxFiles?: number;
  showPreview?: boolean;
  showValidation?: boolean;
  allowExternalUrl?: boolean;
  supportedPlatforms?: string[];
  className?: string;
  children?: React.ReactNode;
  placeholder?: string;
  size?: 'sm' | 'lg';
  // Component isolation props
  instanceId?: string;
  uniqueKey?: string;
}

interface UploadedFile {
  file?: File;
  filePath?: string;
  preview?: string;
  isUploading: boolean;
  error?: string;
  progress: number;
  metadata?: any;
}

const EnhancedFileUpload: React.FC<EnhancedFileUploadProps> = ({
  postType,
  fileCategory = 'file',
  onFileUploaded,
  onValidationError,
  accept,
  multiple = false,
  disabled = false,
  maxFiles = 1,
  showPreview = true,
  showValidation = true,
  allowExternalUrl = true,
  supportedPlatforms = ['YouTube', 'Vimeo', 'Dailymotion', 'Facebook', 'Instagram', 'TikTok'],
  className,
  children,
  placeholder = 'Click to upload or drag files here',
  size,
  instanceId,
  uniqueKey
}) => {
  const { uploadFile, isUploading, uploadProgress, uploadError, clearError } = useFileUpload();
  const { validateFile, getFileExtensionRules } = usePostTypeValidation();
  
  // Generate unique instance identifier
  const componentId = useMemo(() => {
    return instanceId || uniqueKey || `upload-${fileCategory}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, [instanceId, uniqueKey, fileCategory]);
  
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
  const [externalUrl, setExternalUrl] = useState<string>('');
  const [isProcessingUrl, setIsProcessingUrl] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get file extension rules for the current post type
  const fileRules = useMemo(() => {
    return postType ? getFileExtensionRules(postType) : null;
  }, [postType, getFileExtensionRules]);

  // Generate accept attribute based on post type and file category
  const computedAccept = useMemo(() => {
    if (accept) return accept;
    
    // Thumbnails should always be images, regardless of post type
    if (fileCategory === 'thumbnail') {
      return '.jpg,.jpeg,.png,.gif,.webp,.svg';
    }
    
    if (!fileRules) return '*/*';
    
    const extensions = fileRules.allowed.map(ext => `.${ext}`).join(',');
    return extensions;
  }, [accept, fileRules, fileCategory]);

  // Validate external URL
  const validateExternalUrl = useCallback((url: string): string[] => {
    const errors: string[] = [];

    if (!url || url.trim().length === 0) {
      errors.push('URL is required');
      return errors;
    }

    // Basic URL format validation
    try {
      new URL(url);
    } catch (e) {
      errors.push('Invalid URL format');
      return errors;
    }

    // Protocol validation
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      errors.push('URL must start with http:// or https://');
    }

    // Domain validation for supported platforms
    const supportedDomains = [
      'youtube.com', 'youtu.be', 'www.youtube.com',
      'vimeo.com', 'player.vimeo.com',
      'dailymotion.com', 'dai.ly',
      'facebook.com', 'www.facebook.com', 'fb.watch',
      'instagram.com', 'www.instagram.com',
      'twitter.com', 'x.com',
      'tiktok.com', 'www.tiktok.com',
      'soundcloud.com',
      'spotify.com', 'open.spotify.com'
    ];

    const urlObj = new URL(url);
    const domain = urlObj.hostname.toLowerCase();
    const isSupported = supportedDomains.some(supportedDomain => 
      domain === supportedDomain || domain.endsWith(`.${supportedDomain}`)
    );

    if (!isSupported) {
      errors.push(`Domain not supported. Supported platforms: ${supportedPlatforms.join(', ')}`);
    }

    // Post type specific validation
    if (postType === PostType.VIDEO_POST) {
      const videoPatterns = [
        /youtube\.com\/watch/i,
        /youtu\.be\//i,
        /vimeo\.com\/\d+/i,
        /dailymotion\.com\/video/i,
        /facebook\.com\/watch/i,
        /instagram\.com\/p\//i,
        /tiktok\.com\//i
      ];

      const isVideoUrl = videoPatterns.some(pattern => pattern.test(url));
      if (!isVideoUrl) {
        errors.push('URL must be a video URL for video posts');
      }
    }

    return errors;
  }, [postType, supportedPlatforms]);

  // Detect platform from URL
  const detectPlatform = useCallback((url: string): string => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'YouTube';
    if (lowerUrl.includes('vimeo.com')) return 'Vimeo';
    if (lowerUrl.includes('dailymotion.com')) return 'Dailymotion';
    if (lowerUrl.includes('facebook.com')) return 'Facebook';
    if (lowerUrl.includes('instagram.com')) return 'Instagram';
    if (lowerUrl.includes('tiktok.com')) return 'TikTok';
    if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return 'Twitter/X';
    if (lowerUrl.includes('soundcloud.com')) return 'SoundCloud';
    if (lowerUrl.includes('spotify.com')) return 'Spotify';
    return 'External Platform';
  }, []);

  // Detect content type and generate preview data
  const getContentPreview = useCallback((url: string) => {
    const lowerUrl = url.toLowerCase();
    
    // YouTube URLs
    if (lowerUrl.includes('youtube.com/watch') || lowerUrl.includes('youtu.be/')) {
      let videoId = '';
      if (lowerUrl.includes('youtube.com/watch')) {
        videoId = url.split('v=')[1]?.split('&')[0] || '';
      } else if (lowerUrl.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
      }
      
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        title: 'YouTube Video'
      };
    }
    
    // Vimeo URLs
    if (lowerUrl.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('/')[0] || '';
      return {
        type: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${videoId}`,
        thumbnailUrl: `https://vumbnail.com/${videoId}.jpg`,
        title: 'Vimeo Video'
      };
    }
    
    // Direct image URLs
    if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/)) {
      return {
        type: 'image',
        imageUrl: url,
        title: 'Image'
      };
    }
    
    // Fallback for other platforms
    const platform = detectPlatform(url);
    return {
      type: 'external',
      platform,
      url,
      title: `${platform} Content`
    };
  }, [detectPlatform]);

  // Content Preview Component
  const ContentPreview: React.FC<{ url: string; onRemove: () => void }> = ({ url, onRemove }) => {
    const preview = getContentPreview(url);
    
    return (
      <div className="mt-3 p-3 border rounded bg-light">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h6 className="text-dark mb-0">{preview.title}</h6>
          <button
            type="button"
            onClick={onRemove}
            className="btn-close"
            title="Remove URL"
            aria-label="Remove URL"
          />
        </div>
        
        {preview.type === 'youtube' && (
          <div className="mb-2">
            <div className="ratio ratio-16x9 bg-dark rounded overflow-hidden">
              <iframe
                src={preview.embedUrl}
                title="YouTube Video Preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-100 h-100"
              />
            </div>
            <small className="text-muted d-block mt-1">YouTube Video - Click to play</small>
          </div>
        )}
        
        {preview.type === 'vimeo' && (
          <div className="mb-2">
            <div className="ratio ratio-16x9 bg-dark rounded overflow-hidden">
              <iframe
                src={preview.embedUrl}
                title="Vimeo Video Preview"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-100 h-100"
              />
            </div>
            <small className="text-muted d-block mt-1">Vimeo Video - Click to play</small>
          </div>
        )}
        
        {preview.type === 'image' && (
          <div className="mb-2">
            <div className="text-center">
              <img
                src={preview.imageUrl}
                alt="Image Preview"
                className="img-fluid rounded"
                style={{ maxHeight: '200px' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yOCAzMkgxMkM5Ljc5IDMyIDggMzAuMjEgOCAyOFYxMkM4IDkuNzkgOS43OSA4IDEyIDhIMjhDMzAuMjEgOCAzMiA5Ljc5IDMyIDEyVjI4QzMyIDMwLjIxIDMwLjIxIDMyIDI4IDMyWiIgZmlsbD0iI0Q1RDVEOCJ9Ij4KPHBhdGggZD0iTTI2IDE2QzI2IDE3LjEgMjUuMSAxOCAyNCAxOEMyMi45IDE4IDIyIDE3LjEgMjIgMTZDMjIgMTQuOSAyMi45IDE0IDI0IDE0QzI1LjEgMTQgMjYgMTQuOSAyNiAxNloiIGZpbGw9IiM5Q0E0QUUiLz4KPHBhdGggZD0iTTEyIDI2TDE2IDIyTDIwIDI2TDI2IDE4TDMyIDI2VjI4QzMyIDMwLjIxIDMwLjIxIDMyIDI4IDMySDEyQzkuNzkgMzIgOCAzMC4yMSA4IDI4VjI2WiIgZmlsbD0iIzlDQTRBRSIvPgo8L3N2Zz4K';
                }}
              />
            </div>
            <small className="text-muted d-block mt-1">Image Preview</small>
          </div>
        )}
        
        {preview.type === 'external' && (
          <div className="mb-2">
            <div className="d-flex align-items-center p-3 bg-primary bg-opacity-10 rounded border border-primary border-opacity-25">
              <div className="me-2">
                <svg className="text-primary" width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
              <div className="flex-grow-1">
                <div className="fw-medium text-primary">{preview.platform} Content</div>
                <small className="text-primary text-break">{url}</small>
              </div>
            </div>
            <small className="text-muted d-block mt-1">External content from {preview.platform}</small>
          </div>
        )}
        
        <div className="mt-2 pt-2 border-top">
          <small className="text-muted text-break">{url}</small>
        </div>
      </div>
    );
  };

  // Handle external URL submission - SIMPLIFIED APPROACH (No Backend Call)
  const handleExternalUrlSubmit = useCallback(async () => {
    if (!externalUrl.trim()) {
      setValidationErrors(['URL is required']);
      return;
    }

    const urlErrors = validateExternalUrl(externalUrl);
    if (urlErrors.length > 0) {
      setValidationErrors(urlErrors);
      onValidationError?.(urlErrors);
      return;
    }

    setIsProcessingUrl(true);
    setValidationErrors([]);

    try {
      // Frontend-only processing - no backend call needed
      const platform = detectPlatform(externalUrl.trim());
      // Map display platform name to the backend ContentSource code
      const platformToContentSource: Record<string, string> = {
        YouTube: 'YOUTUBE',
        Vimeo: 'VIMEO',
        Dailymotion: 'DAILYMOTION',
        Facebook: 'META',
        Instagram: 'META',
        TikTok: 'TIKTOK',
      };
      const contentSourceCode = platformToContentSource[platform] ?? 'EXTERNAL';
      const urlFile: UploadedFile = {
        filePath: externalUrl.trim(), // Use URL directly as filePath
        isUploading: false,
        progress: 100,
        metadata: {
          isExternalUrl: true,
          originalUrl: externalUrl.trim(),
          platform,
          originalFileName: `${platform} Content`,
          fileType: 'external_url',
          fileCategory: fileCategory || 'file',
          contentSource: contentSourceCode
        }
      };

      setFiles(prev => [...prev, urlFile]);
      onFileUploaded(externalUrl.trim(), urlFile.metadata);
      setExternalUrl('');

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to process external URL';
      setValidationErrors([errorMessage]);
      onValidationError?.([errorMessage]);
    } finally {
      setIsProcessingUrl(false);
    }
  }, [externalUrl, validateExternalUrl, fileCategory, onFileUploaded, onValidationError, detectPlatform]);

  // Validate file before upload
  const validateFileForUpload = useCallback((file: File): string[] => {
    const errors: string[] = [];

    // Special validation for thumbnails - must always be images
    if (fileCategory === 'thumbnail') {
      if (!file.type.startsWith('image/')) {
        errors.push('Thumbnail must be an image file');
        return errors;
      }
      
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedImageTypes.includes(file.type)) {
        errors.push('Supported thumbnail formats: JPG, JPEG, PNG, GIF, WebP, SVG');
        return errors;
      }
      
      // Check file size for thumbnails (max 5MB)
      const maxThumbnailSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxThumbnailSize) {
        errors.push('Thumbnail file size must be less than 5MB');
        return errors;
      }
      
      // Thumbnail validation passed - skip post type validation for thumbnails
    } else if (postType) {
      // Regular file validation using post type rules with fileCategory
      const validation = validateFile(file, postType, fileCategory);
      if (!validation.isValid) {
        errors.push(...validation.errors);
      }
    }

    // Check file count limit
    if (!multiple && files.length >= maxFiles) {
      errors.push(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`);
    }

    return errors;
  }, [postType, validateFile, files.length, maxFiles, multiple, fileCategory]);

  // Handle file selection
  const handleFiles = useCallback(async (selectedFiles: FileList | File[]) => {
    const fileArray = Array.from(selectedFiles);
    
    for (const file of fileArray) {
      const errors = validateFileForUpload(file);
      
      if (errors.length > 0) {
        setValidationErrors(prev => [...prev, ...errors]);
        onValidationError?.(errors);
        continue;
      }

      // Create file preview
      let preview: string | undefined;
      if (showPreview && (file.type.startsWith('image/') || file.type.startsWith('audio/') || file.type.startsWith('video/'))) {
        preview = URL.createObjectURL(file);
      }

      const uploadedFile: UploadedFile = {
        file,
        preview,
        isUploading: true,
        progress: 0
      };

      setFiles(prev => [...prev, uploadedFile]);

      try {
        // Prepare upload request
        const uploadRequest: EnhancedFileUploadRequest = {
          file,
          postType,
          fileCategory,
          metadata: {
            originalName: file.name,
            description: `${fileCategory} for ${postType || 'article'}`,
            contentSource: 'DIRECT'
          }
        };

        const response = await uploadFile(uploadRequest);

        // Update file with upload response
        setFiles(prev => prev.map(f => 
          f.file === file 
            ? { 
                ...f, 
                filePath: response.filePath, 
                isUploading: false, 
                progress: 100,
                metadata: response.metadata 
              }
            : f
        ));

        onFileUploaded(response.filePath, response.metadata);

      } catch (error: any) {
        setFiles(prev => prev.map(f => 
          f.file === file 
            ? { ...f, isUploading: false, error: error.message, progress: 0 }
            : f
        ));
        setValidationErrors(prev => [...prev, error.message]);
        onValidationError?.([error.message]);
      }
    }
  }, [
    validateFileForUpload,
    showPreview,
    postType,
    fileCategory,
    uploadFile,
    onFileUploaded,
    onValidationError
  ]);

  // Handle file input change
  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      handleFiles(selectedFiles);
    }
  }, [handleFiles]);

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles) {
      handleFiles(droppedFiles);
    }
  }, [disabled, handleFiles]);

  // Remove file
  const removeFile = useCallback((index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      const removed = newFiles.splice(index, 1)[0];
      if (removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return newFiles;
    });
  }, []);

  // Clear validation errors
  const clearValidationErrors = useCallback(() => {
    setValidationErrors([]);
    clearError();
  }, [clearError]);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    }
    return `${(bytes / 1024).toFixed(1)}KB`;
  };

  return (
    <div className={classNames('enhanced-file-upload', className)}>
      {/* Upload mode selection */}
      {allowExternalUrl && (
        <div className="mb-3">
          <div className="btn-group w-100" role="group">
            <input
              type="radio"
              className="btn-check"
              name={`uploadMode-${componentId}`}
              id={`upload-file-${componentId}`}
              checked={uploadMode === 'file'}
              onChange={() => setUploadMode('file')}
            />
            <label className="btn btn-outline-primary" htmlFor={`upload-file-${componentId}`}>
              <Icon icon="Upload" className="me-1" />
              Upload File
            </label>

            <input
              type="radio"
              className="btn-check"
              name={`uploadMode-${componentId}`}
              id={`upload-url-${componentId}`}
              checked={uploadMode === 'url'}
              onChange={() => setUploadMode('url')}
            />
            <label className="btn btn-outline-primary" htmlFor={`upload-url-${componentId}`}>
              <Icon icon="Link" className="me-1" />
              External URL
            </label>
          </div>
        </div>
      )}

      {/* Validation info */}
      {showValidation && uploadMode === 'file' && (
        <div className="mb-3">
          {fileCategory === 'thumbnail' ? (
            <small className="text-muted">
              <Icon icon="Info" className="me-1" />
              Thumbnail formats: <strong>JPG, JPEG, PNG, GIF, WebP, SVG</strong> | 
              Max size: <strong>5MB</strong>
            </small>
          ) : postType && fileRules ? (
            <small className="text-muted">
              <Icon icon="Info" className="me-1" />
              Allowed: <strong>{fileRules.allowed.join(', ')}</strong> | 
              Max size: <strong>{formatFileSize(fileRules.maxSize)}</strong>
            </small>
          ) : null}
        </div>
      )}

      {/* External URL input */}
      {allowExternalUrl && uploadMode === 'url' && (
        <div className="mb-3">
          <div className="input-group">
            <span className="input-group-text">
              <Icon icon="Link" />
            </span>
            <input
              type="url"
              id={`external-url-${componentId}`}
              className={classNames('form-control', {
                'is-invalid': validationErrors.length > 0 || uploadError
              })}
              placeholder="Enter external URL (YouTube, Vimeo, etc.)"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              disabled={disabled || isProcessingUrl}
            />
            <Button
              color="primary"
              onClick={handleExternalUrlSubmit}
              isDisable={disabled || isProcessingUrl || !externalUrl.trim()}
              isLight={false}
            >
              {isProcessingUrl ? (
                <>
                  <Icon icon="Sync" className="me-1" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon icon="Add" className="me-1" />
                  Add URL
                </>
              )}
            </Button>
          </div>
          
          {supportedPlatforms.length > 0 && (
            <small className="text-muted mt-1 d-block">
              <Icon icon="Info" className="me-1" />
              Supported platforms: <strong>{supportedPlatforms.join(', ')}</strong>
            </small>
          )}
        </div>
      )}

      {/* File upload area - only show in file mode */}
      {uploadMode === 'file' && (
        <Card
          className={classNames('file-upload-area', {
            'border-primary': dragActive,
            'border-danger': validationErrors.length > 0 || uploadError,
            'opacity-50': disabled
          })}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <CardBody className="text-center p-4">
            {children || (
              <>
                <Icon 
                  icon={isUploading ? 'CloudUpload' : 'CloudUpload'} 
                  size="3x" 
                  color={dragActive ? 'primary' : 'secondary'} 
                  className="mb-3"
                />
                <p className="mb-2">{placeholder}</p>
                {computedAccept !== '*/*' && (
                  <small className="text-muted">
                    Supported formats: {computedAccept.replace(/\./g, '').toUpperCase()}
                  </small>
                )}
              </>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              id={`file-input-${componentId}`}
              accept={computedAccept}
              multiple={multiple}
              onChange={handleFileInputChange}
              disabled={disabled}
              className="d-none"
            />
          </CardBody>
        </Card>
      )}

      {/* Validation errors */}
      {(validationErrors.length > 0 || uploadError) && (
        <Alert color="danger" isLight className="mt-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <Icon icon="Warning" className="me-2" />
              <strong>Upload Issues:</strong>
              <ul className="mb-0 mt-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
                {uploadError && <li>{uploadError}</li>}
              </ul>
            </div>
            <Button
              color="danger"
              size="sm"
              isLight
              onClick={clearValidationErrors}
            >
              Clear
            </Button>
          </div>
        </Alert>
      )}

      {/* Uploaded files preview */}
      {files.length > 0 && (
        <div className="uploaded-files mt-3">
          <h6 className="mb-3">
            <Icon icon="AttachFile" className="me-1" />
            Uploaded Files ({files.length})
          </h6>
          <div className="row">
            {files.map((file, index) => (
              <div key={index} className={file.metadata?.isExternalUrl ? "col-12 mb-3" : "col-md-6 col-lg-4 mb-3"}>
                <Card className="h-100">
                  <CardBody>
                    {/* Preview - Image */}
                    {file.preview && file.file?.type?.startsWith('image/') && (
                      <div className="mb-2">
                        <img 
                          src={file.preview} 
                          alt={file.file?.name || 'Preview'}
                          className="img-fluid rounded"
                          style={{ maxHeight: '100px', width: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    )}

                    {/* Preview - Audio */}
                    {file.preview && file.file?.type?.startsWith('audio/') && (
                      <div className="mb-2 p-3 bg-dark rounded">
                        <div className="d-flex align-items-center mb-2">
                          <Icon icon="MusicNote" color="success" size="2x" className="me-2" />
                          <span className="text-white small">{file.file?.name || 'Audio File'}</span>
                        </div>
                        <audio 
                          src={file.preview} 
                          controls
                          className="w-100"
                          style={{ height: '40px' }}
                        >
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}

                    {/* Preview - Video */}
                    {file.preview && file.file?.type?.startsWith('video/') && (
                      <div className="mb-2">
                        <video 
                          src={file.preview}
                          controls
                          muted
                          className="img-fluid rounded"
                          style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }}
                        >
                          Your browser does not support the video element.
                        </video>
                      </div>
                    )}

                    {/* External URL preview */}
                    {file.metadata?.isExternalUrl && (
                      <ContentPreview 
                        url={file.metadata?.originalUrl || file.filePath || ''} 
                        onRemove={() => removeFile(index)}
                      />
                    )}
                    
                    {/* File info - only show for regular files, not external URLs */}
                    {!file.metadata?.isExternalUrl && (
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <small 
                          className="text-truncate me-2" 
                          title={file.file?.name}
                        >
                          {file.file?.name || 'Unknown File'}
                        </small>
                        <Badge color="light">
                          {formatFileSize(file.file?.size || 0)}
                        </Badge>
                      </div>
                    )}
                    
                    {/* Progress */}
                    {file.isUploading && (
                      <Progress value={file.progress} className="mb-2" />
                    )}
                    
                    {/* Error */}
                    {file.error && (
                      <Alert color="danger" isLight size="sm" className="mb-2">
                        {file.error}
                      </Alert>
                    )}
                    
                    {/* Actions - only show for regular files, not external URLs */}
                    {!file.metadata?.isExternalUrl && (
                      <div className="d-flex justify-content-between align-items-center">
                        {file.filePath && !file.error && (
                          <Badge color="success" isLight>
                            <Icon icon="CheckCircle" className="me-1" />
                            Uploaded
                          </Badge>
                        )}
                        <Button
                          color="danger"
                          size="sm"
                          isLight
                          onClick={() => removeFile(index)}
                          icon="Delete"
                        />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedFileUpload;
